import styled from '@emotion/styled';
import NoSleep from '@uriopass/nosleep.js';
import { useLayoutEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useBackground } from '~/modules/Elements/BackgroundContext';
import NormalizeFontSize from '~/modules/Elements/NormalizeFontSize';
import { switchToTheme } from '~/modules/GameEngine/Drawing/styles';
import { default as events, default as gameEvents } from '~/modules/GameEvents/GameEvents';
import { useEventEffect, useEventListener } from '~/modules/GameEvents/hooks';
import useQueryParam from '~/modules/hooks/useQueryParam';
import LayoutGame from '~/routes/LayoutGame';
import BottomBar from '~/routes/RemoteMic/BottomBar';
import Microphone from '~/routes/RemoteMic/Panels/Microphone';
import ConfirmReadiness from '~/routes/RemoteMic/Panels/Microphone/ConfirmReadiness';
import Ping from '~/routes/RemoteMic/Panels/Microphone/Ping';
import RemoteSettings from '~/routes/RemoteMic/Panels/RemoteSettings';
import RemoteSongList from '~/routes/RemoteMic/Panels/RemoteSongList';
import useSendInitialSongList from '~/routes/RemoteMic/Panels/RemoteSongList/useSendInitialSongList';

const noSleep = new NoSleep();

export type ConnectionStatuses = Parameters<typeof events.karaokeConnectionStatusChange.dispatch>[0] | 'uninitialised';

export type PhoneTabs = 'microphone' | 'song-list' | 'settings';

function RemoteMic() {
  const roomId = useQueryParam('room');

  const [style] = useEventListener(gameEvents.remoteStyleChanged, true) ?? ['regular'];

  useLayoutEffect(() => {
    switchToTheme(style);
  }, [style]);

  // Mantén esto (forma parte del flujo y estilo remoto)
  useBackground(true, style);

  const [activeTab, setActiveTab] = useState<PhoneTabs>('microphone');

  const [connectionStatus, connectionError] = useEventListener(events.karaokeConnectionStatusChange) ?? [
    'uninitialised',
  ];

  const [monitoringStarted, setMonitoringStarted] = useState(false);
  useEventEffect(events.micMonitoringStarted, () => setMonitoringStarted(true));
  useEventEffect(events.micMonitoringStopped, () => setMonitoringStarted(false));

  useSendInitialSongList(connectionStatus === 'connected');

  const [isKeepAwakeOn, setIsKeepAwakeOn] = useState(false);

  const setKeepAwake = async (turnOn: boolean) => {
    try {
      if (turnOn && !noSleep.isEnabled) {
        await noSleep.enable();
      } else if (!turnOn && noSleep.isEnabled) {
        await noSleep.disable();
      }
      setIsKeepAwakeOn(turnOn);
    } catch (e) {
      console.warn("Couldn't set wakelock", e);
    }
  };

  const onConfirm = () => {
    setKeepAwake(true);
  };

  return (
    <>
      <Helmet>
        <title>Remote Mic | fastKaraoke</title>
      </Helmet>

      <LayoutGame
        toolbarContent={
          activeTab !== 'song-list' ? (
            <ConnectionSummary>
              <StatusLabel>Connection</StatusLabel>
              <StatusValue>
                <strong data-test="connection-status">{connectionStatus?.toUpperCase()}</strong>
                {connectionStatus === 'connected' && <Ping />}
              </StatusValue>
            </ConnectionSummary>
          ) : undefined
        }>
        {/* Fondo fastKaraoke */}
        <BackgroundShell
          style={{
            backgroundImage: `url("/menu_background_blur.png")`,
          }}>
          <Overlay />

          <Content>
            <ConfirmReadiness onConfirm={onConfirm} />
            <NormalizeFontSize size={12} scopeSelector="#phone-ui-container" />

            <PhoneCard id="phone-ui-container">
              <div className="flex flex-1 flex-col justify-center overflow-hidden">
                {activeTab === 'microphone' && (
                  <Microphone
                    roomId={roomId}
                    monitoringStarted={monitoringStarted}
                    setMonitoringStarted={setMonitoringStarted}
                    connectionStatus={connectionStatus}
                    setIsKeepAwakeOn={setIsKeepAwakeOn}
                    isKeepAwakeOn={isKeepAwakeOn}
                    connectionError={connectionError}
                  />
                )}

                {activeTab === 'song-list' && (
                  <RemoteSongList
                    roomId={roomId}
                    monitoringStarted={monitoringStarted}
                    setMonitoringStarted={setMonitoringStarted}
                    connectionStatus={connectionStatus}
                    setIsKeepAwakeOn={setIsKeepAwakeOn}
                    isKeepAwakeOn={isKeepAwakeOn}
                    connectionError={connectionError}
                  />
                )}

                {activeTab === 'settings' && (
                  <RemoteSettings
                    roomId={roomId}
                    monitoringStarted={monitoringStarted}
                    setMonitoringStarted={setMonitoringStarted}
                    connectionStatus={connectionStatus}
                    setIsKeepAwakeOn={setIsKeepAwakeOn}
                    isKeepAwakeOn={isKeepAwakeOn}
                    connectionError={connectionError}
                  />
                )}
              </div>

              <BottomBar setActiveTab={setActiveTab} active={activeTab} />
            </PhoneCard>
          </Content>
        </BackgroundShell>
      </LayoutGame>
    </>
  );
}

export default RemoteMic;

const ConnectionSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 118px;
  color: var(--walky-ink);
`;

const StatusLabel = styled.span`
  color: var(--walky-muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
`;

const StatusValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--walky-ink);
  font-size: 14px;
  font-weight: 800;
  line-height: 1;
`;

const BackgroundShell = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100vw;

  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: var(--walky-ink);
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  background: radial-gradient(circle at 50% 0%, rgba(244, 90, 10, 0.2), transparent 34%), rgba(2, 7, 16, 0.58);
  backdrop-filter: blur(3px);
`;

const Content = styled.div`
  position: relative;
  z-index: 1;

  min-height: 100vh;
  width: 100vw;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: clamp(10px, 3vw, 18px);
`;

const PhoneCard = styled.div`
  width: min(430px, 100%);
  max-height: calc(100vh - 2rem);
  min-height: min(560px, calc(100vh - 2rem));

  display: flex;
  flex-direction: column;
  overflow: hidden;

  border: 1px solid var(--walky-border);
  border-top: 3px solid var(--walky-orange);
  border-radius: 22px;
  background: var(--walky-surface-translucent);
  color: var(--walky-ink);
  backdrop-filter: blur(18px);
  box-shadow: 0 22px 70px rgba(0, 0, 0, 0.4);

  @media (max-width: 420px) {
    min-height: 0;
    max-height: calc(100vh - 1.5rem);
    border-radius: 16px;
  }

  @media (max-height: 560px) {
    max-height: calc(100vh - 1.5rem);
    min-height: 0;
  }

  @media (max-height: 500px) and (min-aspect-ratio: 16/10) {
    width: min(960px, 100%);
  }
`;
